/**
 * IsoCostTemplatesView — Super Admin only.
 * Same table + assignment (assign template → ISO for a processor).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CostTemplatesTable } from './ProcessorCostTemplatesView';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Link2 } from 'lucide-react';
import api from '../../lib/api';

export default function IsoCostTemplatesView() {
  const [showAssign, setShowAssign] = useState(false);
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setShowAssign(true)} data-testid="assign-iso-cost-btn">
          <Link2 className="h-3 w-3 mr-1" /> Assign to ISO
        </Button>
      </div>
      <CostTemplatesTable variant="iso" />
      {showAssign && <AssignDialog onClose={() => setShowAssign(false)} />}
    </div>
  );
}

function AssignDialog({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [isos, setIsos] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [isoUserId, setIsoUserId] = useState('');
  const [processorSlug, setProcessorSlug] = useState('maverick');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tpls, downline] = await Promise.all([
          api.getIsoCostTemplates(),
          api.getScheduleADownlineManager(),
        ]);
        setTemplates(tpls || []);
        setIsos((downline.children || []).filter(u => u.role === 'iso'));
      } catch (e) {
        toast.error('Failed to load');
      }
    })();
  }, []);

  const save = async () => {
    if (!templateId || !isoUserId) {
      toast.error('Pick a template and ISO');
      return;
    }
    setSaving(true);
    try {
      await api.assignIsoCostTemplate({
        template_id: templateId, iso_user_id: isoUserId,
        processor_slug: processorSlug, effective_date: effectiveDate,
      });
      toast.success('Assigned');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="assign-iso-dialog">
        <DialogHeader>
          <DialogTitle>Assign ISO Cost Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>ISO</Label>
            <Select value={isoUserId} onValueChange={setIsoUserId}>
              <SelectTrigger><SelectValue placeholder="Pick an ISO..." /></SelectTrigger>
              <SelectContent>
                {isos.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Processor</Label>
            <Select value={processorSlug} onValueChange={setProcessorSlug}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="maverick">TSYS</SelectItem>
                <SelectItem value="fiserv">Fiserv</SelectItem>
                <SelectItem value="north_epx">Global/EPX</SelectItem>
                <SelectItem value="square">Square</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue placeholder="Pick a template..." /></SelectTrigger>
              <SelectContent>
                {templates.filter(t => t.processor_slug === processorSlug).map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name} (v{t.version})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Effective Date</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Assign'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
